/**
 * 🏥 COMPREHENSIVE HEALTH SCORE CALCULATOR
 * 
 * Calculates an overall health percentage based on medical standards and research
 * 
 * Based on:
 * - American Heart Association guidelines
 * - CDC physical activity recommendations
 * - Sleep Foundation research
 * - WHO stress and wellness metrics
 */

export class HealthScoreCalculator {
  constructor() {
    // 📊 Health category weights (totaling 100%)
    this.categoryWeights = {
      vitals: 25,       // Heart rate, blood oxygen, temperature
      activity: 20,     // Steps, calories, exercise
      sleep: 20,        // Sleep quality and duration
      wellness: 15,     // Stress, hydration, mindfulness
      goals: 10,        // Goal achievement
      consistency: 10   // Data reliability and completeness
    };

    // 🎯 Target ranges for optimal health
    this.optimalRanges = {
      heartRate: { min: 60, max: 100, restingMin: 50, restingMax: 70 },
      bloodOxygen: { min: 95, max: 100 },
      temperature: { min: 97.8, max: 99.1 },
      steps: { min: 8000, optimal: 10000 },
      activeMinutes: { min: 30, optimal: 60 },
      sleep: { min: 7, max: 9, optimal: 8 },
      sleepEfficiency: { min: 85, optimal: 95 },
      stress: { low: 30, moderate: 60, high: 80 },
      hydration: { min: 2000, optimal: 2500 } // ml per day
    };
  }

  /**
   * 🧮 MAIN HEALTH SCORE CALCULATION
   */
  calculateOverallHealthScore(healthData) {
    const scores = {
      vitals: this.calculateVitalsScore(healthData.healthMetrics?.vitalSigns),
      activity: this.calculateActivityScore(healthData.healthMetrics?.activity),
      sleep: this.calculateSleepScore(healthData.healthMetrics?.sleep?.sleep),
      wellness: this.calculateWellnessScore(healthData.healthMetrics?.wellness),
      goals: this.calculateGoalsScore(healthData.healthMetrics),
      consistency: this.calculateConsistencyScore(healthData.businessIntelligence)
    };

    // Calculate weighted average
    let totalScore = 0;
    let totalWeight = 0;

    Object.entries(scores).forEach(([category, score]) => {
      if (score !== null) {
        totalScore += score * this.categoryWeights[category];
        totalWeight += this.categoryWeights[category];
      }
    });

    const overallScore = totalWeight > 0 ? Math.round(totalScore / totalWeight) : 0;

    return {
      overallScore,
      level: this.getHealthLevel(overallScore),
      categoryScores: scores,
      breakdown: this.generateScoreBreakdown(scores),
      recommendations: this.generateRecommendations(scores, healthData)
    };
  }

  /**
   * 💓 VITALS SCORE (25% weight)
   */
  calculateVitalsScore(vitals) {
    if (!vitals) return null;

    let vitalScores = [];

    // Heart Rate Score (40% of vitals)
    if (vitals.heartRate) {
      const hr = vitals.heartRate.value;
      let hrScore = 0;
      
      if (hr >= this.optimalRanges.heartRate.min && hr <= this.optimalRanges.heartRate.max) {
        hrScore = 100;
      } else if (hr < this.optimalRanges.heartRate.min) {
        hrScore = Math.max(0, 60 + (hr - 50) * 2); // Bradycardia penalty
      } else {
        hrScore = Math.max(0, 100 - (hr - 100) * 2); // Tachycardia penalty
      }
      
      vitalScores.push({ score: hrScore, weight: 0.4 });
    }

    // Blood Oxygen Score (35% of vitals)
    if (vitals.bloodOxygen) {
      const spo2 = vitals.bloodOxygen.value;
      let spo2Score = 0;
      
      if (spo2 >= 98) {
        spo2Score = 100;
      } else if (spo2 >= 95) {
        spo2Score = 80 + (spo2 - 95) * 6.67; // 80-100 range
      } else {
        spo2Score = Math.max(0, spo2 * 0.8); // Below normal penalty
      }
      
      vitalScores.push({ score: spo2Score, weight: 0.35 });
    }

    // Body Temperature Score (25% of vitals)
    if (vitals.bodyTemperature) {
      const temp = vitals.bodyTemperature.value;
      let tempScore = 0;
      
      if (temp >= this.optimalRanges.temperature.min && temp <= this.optimalRanges.temperature.max) {
        tempScore = 100;
      } else {
        const deviation = Math.abs(temp - 98.4); // Normal temp
        tempScore = Math.max(0, 100 - deviation * 10);
      }
      
      vitalScores.push({ score: tempScore, weight: 0.25 });
    }

    return this.calculateWeightedAverage(vitalScores);
  }

  /**
   * 🏃‍♀️ ACTIVITY SCORE (20% weight)
   */
  calculateActivityScore(activity) {
    if (!activity) return null;

    let activityScores = [];

    // Steps Score (40% of activity)
    if (activity.steps) {
      const steps = activity.steps.value;
      let stepsScore = 0;
      
      if (steps >= this.optimalRanges.steps.optimal) {
        stepsScore = 100;
      } else if (steps >= this.optimalRanges.steps.min) {
        stepsScore = 70 + (steps - this.optimalRanges.steps.min) / (this.optimalRanges.steps.optimal - this.optimalRanges.steps.min) * 30;
      } else {
        stepsScore = (steps / this.optimalRanges.steps.min) * 70;
      }
      
      activityScores.push({ score: stepsScore, weight: 0.4 });
    }

    // Active Minutes Score (35% of activity)
    if (activity.activeMinutes) {
      const minutes = activity.activeMinutes.value;
      let minutesScore = 0;
      
      if (minutes >= this.optimalRanges.activeMinutes.optimal) {
        minutesScore = 100;
      } else if (minutes >= this.optimalRanges.activeMinutes.min) {
        minutesScore = 80 + (minutes - this.optimalRanges.activeMinutes.min) / (this.optimalRanges.activeMinutes.optimal - this.optimalRanges.activeMinutes.min) * 20;
      } else {
        minutesScore = (minutes / this.optimalRanges.activeMinutes.min) * 80;
      }
      
      activityScores.push({ score: minutesScore, weight: 0.35 });
    }

    // Calorie Goals Score (25% of activity)
    if (activity.activeCalories && activity.activeCalories.goal) {
      const percentage = activity.activeCalories.percentage;
      let calorieScore = 0;
      
      if (percentage >= 1.0) {
        calorieScore = 100;
      } else {
        calorieScore = percentage * 100;
      }
      
      activityScores.push({ score: calorieScore, weight: 0.25 });
    }

    return this.calculateWeightedAverage(activityScores);
  }

  /**
   * 😴 SLEEP SCORE (20% weight)
   */
  calculateSleepScore(sleep) {
    if (!sleep) return null;

    let sleepScores = [];

    // Sleep Duration Score (50% of sleep)
    if (sleep.totalSleep) {
      const hours = sleep.totalSleep;
      let durationScore = 0;
      
      if (hours >= this.optimalRanges.sleep.min && hours <= this.optimalRanges.sleep.max) {
        durationScore = 100;
      } else {
        const deviation = Math.abs(hours - this.optimalRanges.sleep.optimal);
        durationScore = Math.max(0, 100 - deviation * 15);
      }
      
      sleepScores.push({ score: durationScore, weight: 0.5 });
    }

    // Sleep Efficiency Score (30% of sleep)
    if (sleep.sleepEfficiency) {
      const efficiency = sleep.sleepEfficiency;
      let efficiencyScore = 0;
      
      if (efficiency >= this.optimalRanges.sleepEfficiency.optimal) {
        efficiencyScore = 100;
      } else if (efficiency >= this.optimalRanges.sleepEfficiency.min) {
        efficiencyScore = 70 + (efficiency - this.optimalRanges.sleepEfficiency.min) / (this.optimalRanges.sleepEfficiency.optimal - this.optimalRanges.sleepEfficiency.min) * 30;
      } else {
        efficiencyScore = (efficiency / this.optimalRanges.sleepEfficiency.min) * 70;
      }
      
      sleepScores.push({ score: efficiencyScore, weight: 0.3 });
    }

    // Sleep Quality Score (20% of sleep)
    if (sleep.quality) {
      let qualityScore = 0;
      
      switch (sleep.quality.toLowerCase()) {
        case 'excellent':
          qualityScore = 100;
          break;
        case 'good':
          qualityScore = 85;
          break;
        case 'fair':
          qualityScore = 60;
          break;
        case 'poor':
          qualityScore = 30;
          break;
        default:
          qualityScore = 50;
      }
      
      sleepScores.push({ score: qualityScore, weight: 0.2 });
    }

    return this.calculateWeightedAverage(sleepScores);
  }

  /**
   * 🧘‍♀️ WELLNESS SCORE (15% weight)
   */
  calculateWellnessScore(wellness) {
    if (!wellness) return null;

    let wellnessScores = [];

    // Stress Score (40% of wellness)
    if (wellness.stress) {
      const stress = wellness.stress.value;
      let stressScore = 0;
      
      if (stress <= this.optimalRanges.stress.low) {
        stressScore = 100;
      } else if (stress <= this.optimalRanges.stress.moderate) {
        stressScore = 100 - (stress - this.optimalRanges.stress.low) / (this.optimalRanges.stress.moderate - this.optimalRanges.stress.low) * 30;
      } else if (stress <= this.optimalRanges.stress.high) {
        stressScore = 70 - (stress - this.optimalRanges.stress.moderate) / (this.optimalRanges.stress.high - this.optimalRanges.stress.moderate) * 40;
      } else {
        stressScore = Math.max(0, 30 - (stress - this.optimalRanges.stress.high));
      }
      
      wellnessScores.push({ score: stressScore, weight: 0.4 });
    }

    // Hydration Score (35% of wellness)
    if (wellness.hydration) {
      const hydration = wellness.hydration.value;
      let hydrationScore = 0;
      
      if (hydration >= this.optimalRanges.hydration.optimal) {
        hydrationScore = 100;
      } else if (hydration >= this.optimalRanges.hydration.min) {
        hydrationScore = 80 + (hydration - this.optimalRanges.hydration.min) / (this.optimalRanges.hydration.optimal - this.optimalRanges.hydration.min) * 20;
      } else {
        hydrationScore = (hydration / this.optimalRanges.hydration.min) * 80;
      }
      
      wellnessScores.push({ score: hydrationScore, weight: 0.35 });
    }

    // Mindfulness Score (25% of wellness)
    if (wellness.mindfulness) {
      const sessions = wellness.mindfulness.sessionCount || 0;
      const minutes = wellness.mindfulness.totalMinutes || 0;
      
      let mindfulnessScore = 0;
      if (sessions > 0 && minutes >= 5) {
        mindfulnessScore = Math.min(100, 60 + sessions * 10 + minutes * 2);
      } else if (sessions > 0) {
        mindfulnessScore = 40;
      } else {
        mindfulnessScore = 20; // Base score for no practice
      }
      
      wellnessScores.push({ score: mindfulnessScore, weight: 0.25 });
    }

    return this.calculateWeightedAverage(wellnessScores);
  }

  /**
   * 🎯 GOALS ACHIEVEMENT SCORE (10% weight)
   */
  calculateGoalsScore(healthMetrics) {
    if (!healthMetrics) return null;

    let goalScores = [];

    // Check various goal achievements
    const goals = [
      healthMetrics.activity?.steps,
      healthMetrics.activity?.activeCalories,
      healthMetrics.activity?.activeMinutes,
      healthMetrics.activity?.standHours,
      healthMetrics.wellness?.hydration
    ].filter(metric => metric && metric.goal && metric.percentage);

    if (goals.length === 0) return 70; // Default if no goals set

    goals.forEach(metric => {
      let goalScore = 0;
      const achievement = metric.percentage;
      
      if (achievement >= 1.0) {
        goalScore = 100;
      } else if (achievement >= 0.8) {
        goalScore = 80 + (achievement - 0.8) * 100;
      } else {
        goalScore = achievement * 80;
      }
      
      goalScores.push({ score: goalScore, weight: 1 / goals.length });
    });

    return this.calculateWeightedAverage(goalScores);
  }

  /**
   * 📊 DATA CONSISTENCY SCORE (10% weight)
   */
  calculateConsistencyScore(businessIntelligence) {
    if (!businessIntelligence) return 70;

    let consistencyScore = 0;
    
    // Data Quality (40%)
    if (businessIntelligence.dataQuality) {
      consistencyScore += businessIntelligence.dataQuality * 100 * 0.4;
    }
    
    // Completeness (35%)
    if (businessIntelligence.completeness) {
      consistencyScore += businessIntelligence.completeness * 0.35;
    }
    
    // Reliability (25%)
    if (businessIntelligence.reliability) {
      let reliabilityScore = 0;
      switch (businessIntelligence.reliability.toLowerCase()) {
        case 'high':
          reliabilityScore = 100;
          break;
        case 'medium':
          reliabilityScore = 75;
          break;
        case 'low':
          reliabilityScore = 50;
          break;
        default:
          reliabilityScore = 60;
      }
      consistencyScore += reliabilityScore * 0.25;
    }

    return Math.round(consistencyScore);
  }

  /**
   * 🔢 HELPER METHODS
   */
  calculateWeightedAverage(scores) {
    if (scores.length === 0) return null;
    
    let totalScore = 0;
    let totalWeight = 0;
    
    scores.forEach(({ score, weight }) => {
      totalScore += score * weight;
      totalWeight += weight;
    });
    
    return totalWeight > 0 ? Math.round(totalScore / totalWeight) : 0;
  }

  getHealthLevel(score) {
    if (score >= 90) return 'Excellent';
    if (score >= 80) return 'Very Good';
    if (score >= 70) return 'Good';
    if (score >= 60) return 'Fair';
    if (score >= 50) return 'Needs Improvement';
    return 'Poor';
  }

  generateScoreBreakdown(scores) {
    return Object.entries(scores)
      .filter(([_, score]) => score !== null)
      .map(([category, score]) => ({
        category: category.charAt(0).toUpperCase() + category.slice(1),
        score,
        weight: this.categoryWeights[category],
        status: this.getHealthLevel(score)
      }));
  }

  generateRecommendations(scores, healthData) {
    const recommendations = [];

    // Vitals recommendations
    if (scores.vitals !== null && scores.vitals < 80) {
      recommendations.push({
        category: 'Vitals',
        priority: 'high',
        message: 'Consider consulting with a healthcare provider about your vital signs',
        action: 'Schedule medical checkup'
      });
    }

    // Activity recommendations
    if (scores.activity !== null && scores.activity < 70) {
      recommendations.push({
        category: 'Activity',
        priority: 'medium',
        message: 'Increase daily physical activity to improve overall health',
        action: 'Set higher step and exercise goals'
      });
    }

    // Sleep recommendations
    if (scores.sleep !== null && scores.sleep < 75) {
      recommendations.push({
        category: 'Sleep',
        priority: 'medium',
        message: 'Improve sleep quality and duration for better recovery',
        action: 'Maintain consistent sleep schedule'
      });
    }

    // Wellness recommendations
    if (scores.wellness !== null && scores.wellness < 75) {
      recommendations.push({
        category: 'Wellness',
        priority: 'medium',
        message: 'Focus on stress management and hydration',
        action: 'Practice mindfulness and drink more water'
      });
    }

    return recommendations;
  }
}

/**
 * 🧪 DEMO USAGE WITH YOUR DATA
 */
export function demonstrateHealthScoreCalculation() {
  const calculator = new HealthScoreCalculator();
  
  // Sample data from your Apple Watch
  const sampleHealthData = {
    healthMetrics: {
      vitalSigns: {
        heartRate: { value: 99 },
        bloodOxygen: { value: 99 },
        bodyTemperature: { value: 99.2 }
      },
      activity: {
        steps: { value: 11256, goal: 10000, percentage: 1.13 },
        activeCalories: { value: 684, goal: 600, percentage: 1.14 },
        activeMinutes: { value: 76, goal: 30, percentage: 2.53 },
        standHours: { value: 11, goal: 12, percentage: 0.92 }
      },
      sleep: {
        sleep: {
          totalSleep: 7.6,
          sleepEfficiency: 86,
          quality: 'good'
        }
      },
      wellness: {
        stress: { value: 39 },
        hydration: { value: 2424, goal: 2000, percentage: 121 },
        mindfulness: { sessionCount: 1, totalMinutes: 0 }
      }
    },
    businessIntelligence: {
      dataQuality: 0.9875,
      completeness: 99,
      reliability: 'medium'
    }
  };

  const healthScore = calculator.calculateOverallHealthScore(sampleHealthData);
  
  console.log('🏥 HEALTH SCORE ANALYSIS');
  console.log('========================');
  console.log(`Overall Health Score: ${healthScore.overallScore}% (${healthScore.level})`);
  console.log('');
  console.log('Category Breakdown:');
  healthScore.breakdown.forEach(category => {
    console.log(`  ${category.category}: ${category.score}% (${category.status}) - Weight: ${category.weight}%`);
  });
  console.log('');
  console.log('Recommendations:');
  healthScore.recommendations.forEach(rec => {
    console.log(`  ${rec.category}: ${rec.message}`);
  });

  return healthScore;
}

// Note: Demo runner disabled in browser bundles; keep callable via explicit import
// If needed in Node, guard the direct-run with typeof checks to avoid Webpack errors
if (typeof require !== 'undefined' && typeof module !== 'undefined') {
  if (require.main === module) {
    demonstrateHealthScoreCalculation();
  }
}
